import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Snackbar,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { shiftsApi } from '../services/api';
import { useTranslation } from 'react-i18next';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { ro, enUS } from 'date-fns/locale';

const ReportPanel = () => {
  const currentDate = new Date();
  const [startDate, setStartDate] = useState(currentDate);
  const [endDate, setEndDate] = useState(currentDate);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [startDateError, setStartDateError] = useState(null);
  const [endDateError, setEndDateError] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const { t, i18n } = useTranslation();

  const currentLocale = i18n.language === 'ro' ? ro : enUS;
  const dateFormat = i18n.language === 'ro' ? 'dd.MM.yyyy' : 'MM/dd/yyyy';

  // Load employees by date range whenever dates change
  useEffect(() => {
    const fetchEmployeesByDateRange = async () => {
      if (!validateDateRange()) return;
      
      try {
        setLoadingEmployees(true);
        const response = await shiftsApi.getEmployeesByDateRange(startDate, endDate);
        
        if (!response.data || !Array.isArray(response.data)) {
          console.error('Invalid employee data format:', response.data);
          return;
        }
        
        // console.log('Fetched employees for date range:', response.data);
        setEmployees(response.data);
        
        // Reset selected employee if the current selection is no longer in the list
        if (selectedEmployee !== 'all' && !response.data.includes(selectedEmployee)) {
          setSelectedEmployee('all');
        }
      } catch (err) {
        console.error('Failed to fetch employees by date range:', err);
      } finally {
        setLoadingEmployees(false);
      }
    };
    
    fetchEmployeesByDateRange();
  }, [startDate, endDate]);

  const validateDateRange = () => {
    return startDate <= endDate;
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
    if (date > endDate) {
      setStartDateError(t('reports.errors.startDateAfterEnd'));
    } else {
      setStartDateError(null);
      setEndDateError(null);
    }
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    if (date < startDate) {
      setEndDateError(t('reports.errors.endDateBeforeStart'));
    } else {
      setStartDateError(null);
      setEndDateError(null);
    }
  };

  const handleEmployeeChange = (event) => {
    setSelectedEmployee(event.target.value);
  };

  const handleGenerateReport = async () => {
    try {
      setLoading(true);
      
      let response;
      let filePrefix;
      let fileName;
      const preposition = i18n.language === 'ro' ? 'pânã-în' : 'to';
      
      if (selectedEmployee === 'all') {
        // Generate complete report for all employees
        response = await shiftsApi.generateCompleteReport(
          format(startDate, 'dd.MM.yyyy'),
          format(endDate, 'dd.MM.yyyy'),
          i18n.language
        );
        filePrefix = i18n.language === 'ro' ? 'pontaj' : 'timesheet';
        fileName = `${filePrefix}-${format(startDate, 'yyyy-MM-dd')}-${preposition}-${format(endDate, 'yyyy-MM-dd')}.pdf`;
      } else {
        // Generate report for selected employee
        response = await shiftsApi.generateEmployeeReport(
          format(startDate, 'dd.MM.yyyy'),
          format(endDate, 'dd.MM.yyyy'),
          selectedEmployee,
          i18n.language
        );
        filePrefix = i18n.language === 'ro' ? 'pontaj-persoana' : 'timesheet-person';
        fileName = `${filePrefix}-${selectedEmployee}-${format(startDate, 'yyyy-MM-dd')}-${preposition}-${format(endDate, 'yyyy-MM-dd')}.pdf`;
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError(t('reports.generateError'));
      console.error('Failed to generate report:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom>
        {t('reports.title')}
      </Typography>

      <Box sx={{ mb: 4 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={currentLocale}>
          <Grid container spacing={2} sx={{ mb: 2 }} direction={{ xs: 'column', sm: 'row' }}>
            <Grid item xs={12} sm={6} md={4} lg={6}>
              <DatePicker
                label={t('reports.startDate')}
                value={startDate}
                onChange={handleStartDateChange}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    error: !!startDateError,
                    helperText: startDateError,
                    sx: { 
                      width: '100%',
                      '& .MuiInputBase-root': {
                        fontSize: '0.9rem'
                      }
                    }
                  }
                }}
                format={dateFormat}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={6}>
              <DatePicker
                label={t('reports.endDate')}
                value={endDate}
                onChange={handleEndDateChange}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    error: !!endDateError,
                    helperText: endDateError,
                    sx: { 
                      width: '100%',
                      '& .MuiInputBase-root': {
                        fontSize: '0.9rem'
                      }
                    }
                  }
                }}
                format={dateFormat}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={4} lg={12}>
              <FormControl fullWidth size="small">
                <InputLabel id="employee-select-label" size="small">
                  {t('reports.selectEmployee')}
                </InputLabel>
                <Select
                  labelId="employee-select-label"
                  value={selectedEmployee}
                  onChange={handleEmployeeChange}
                  fullWidth
                  size="small"
                  label={t('reports.selectEmployee')}
                  disabled={loadingEmployees}
                  startAdornment={loadingEmployees ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : null}
                >
                  <MenuItem value="all">
                    {t('reports.allEmployees')}
                  </MenuItem>
                  {employees.length === 0 ? (
                    <MenuItem disabled value="">
                      {t('reports.noEmployees')}
                    </MenuItem>
                  ) : (
                    employees.map((employee) => (
                      <MenuItem key={employee} value={employee}>
                        {employee}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </LocalizationProvider>

        <Button
          variant="contained"
          fullWidth
          onClick={handleGenerateReport}
          disabled={loading || !!startDateError || !!endDateError || !validateDateRange() || loadingEmployees}
        >
          {loading ? t('reports.generating') : t('reports.generatePdf')}
        </Button>
      </Box>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReportPanel; 